import { Link, useRouterState } from "@tanstack/react-router";
import { Lock, Loader2 } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

/** Routes a signed-in user can always reach, even without an active plan. */
const ALWAYS_ALLOWED = ["/settings", "/pricing", "/guide"];

export function RequireSubscription({ children }: { children: React.ReactNode }) {
  const { loading, isActive, status } = useSubscription();
  const path = useRouterState({ select: (s) => s.location.pathname });

  if (ALWAYS_ALLOWED.some((p) => path.startsWith(p))) return <>{children}</>;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-cyan" />
      </div>
    );
  }

  if (isActive) return <>{children}</>;

  const canceled = status === "canceled";
  const unpaid = status === "past_due" || status === "paused";
  const title = unpaid
    ? "Payment required"
    : canceled
      ? "Subscription canceled"
      : "Subscription required";
  const body = unpaid
    ? "We couldn't charge your card, so console modules are locked. Update your payment method and settle the outstanding invoice to restore access instantly — your Vault cases stay intact."
    : canceled
      ? "Your plan was canceled, so console modules are locked. Resubscribe to restore access — your Vault cases stay intact."
      : "Start your 1-day free trial to unlock scans, the Vault, AI copilot and every intelligence module. A card is required to start the trial — cancel before it ends and you won't be charged.";
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="glass w-full max-w-lg rounded-xl p-6 text-center">
        <div className="mx-auto grid h-11 w-11 place-items-center rounded-md border border-amber/40 bg-amber/10">
          <Lock className="h-5 w-5 text-amber" />
        </div>
        <h1 className="mt-4 font-display text-2xl">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <Link
            to="/pricing"
            className="rounded-md border border-cyan/40 bg-cyan/10 px-4 py-2 text-sm text-cyan hover:bg-cyan/20"
          >
            {unpaid ? "Change plan" : canceled ? "Resubscribe" : "Choose a plan"}
          </Link>
          <Link
            to="/settings"
            className="rounded-md border border-panel-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            {unpaid ? "Update payment method" : "Billing settings"}
          </Link>
        </div>
      </div>
    </div>
  );
}
